/**
 * Reporting Pathway Report (MVP module Phase F).
 * Ranked projection of Traditional MIPS + all 27 MVPs for the selected practice,
 * with dollars, confidence flags, pathway detail modal, and a what-if panel.
 * Every number is a PROJECTION — labeled as such throughout.
 * Spec: docs/reference/mvp/IMPLEMENTATION_PLAN.md §8-F.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useProgramYear } from '@/contexts/ProgramYearContext';
import { MipsDataService } from '@/lib/mips-data-service';
import { useMvpPathwayReport } from '@/lib/mvp/assemble-inputs';
import { useSubgroups } from '@/hooks/queries/useMvpQueries';
import type { Assumptions, PathwayResult } from '@/lib/mvp/types';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  ArrowLeft, ChevronRight, FlaskConical, Info, Trophy,
} from 'lucide-react';

const fmtMoney = (v: number) => {
  const abs = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(Math.abs(v));
  return v < 0 ? `-${abs}` : v > 0 ? `+${abs}` : abs;
};
const fmtPct = (v: number | null, digits = 1) => (v == null ? '—' : `${v.toFixed(digits)}%`);

const FLAG_LABELS: Record<string, { label: string; tone: 'warn' | 'info' }> = {
  'no-historical-benchmark': { label: 'No benchmark', tone: 'warn' },
  'marginal-case-min': { label: 'Marginal case min', tone: 'warn' },
  'stale-cost-year': { label: 'Prior-year cost', tone: 'info' },
  'carried-forward-cpb': { label: 'CPB carried forward', tone: 'info' },
  'reconstructed-improvement': { label: 'Improvement reconstructed', tone: 'info' },
  'terminal-override-75': { label: 'Scored 75 by rule', tone: 'warn' },
  'estimate': { label: 'Estimate', tone: 'info' },
};

function confidence(row: PathwayResult): 'High' | 'Medium' | 'Low' {
  const warns = row.flags.filter(f => FLAG_LABELS[f]?.tone === 'warn').length;
  if (warns >= 2) return 'Low';
  if (warns === 1 || row.flags.length >= 3) return 'Medium';
  return 'High';
}

export default function PathwayReport() {
  const navigate = useNavigate();
  const { selectedPractice } = useAuth();
  const { programYear } = useProgramYear();
  const practiceId = selectedPractice?.id;

  const [whatIfOpen, setWhatIfOpen] = useState(false);
  const [assumptions, setAssumptions] = useState<Assumptions>({});
  const [detailPathwayId, setDetailPathwayId] = useState<string | null>(null);
  // C2: entity scope — group (TIN) or one of the practice's subgroups. Subgroup
  // runs use derived quality counts + roster-summed dollars (estimate-flagged).
  const [entityKey, setEntityKey] = useState<string>('group');
  const subgroupsQ = useSubgroups(practiceId, programYear);
  const subgroupId = entityKey.startsWith('sg:') ? Number(entityKey.slice(3)) : null;
  const activeSubgroup = (subgroupsQ.data ?? []).find(sg => sg.id === subgroupId) ?? null;

  const { report, loading, ready, meta } = useMvpPathwayReport(practiceId, programYear, assumptions, subgroupId);

  const dataService = MipsDataService.getInstance();
  const titleFor = useMemo(() => {
    const map = new Map<string, string>();
    dataService.getMvps(programYear).forEach(m => map.set(m.mvpId, m.title));
    return (id: string) => (id === 'traditional' ? 'Traditional MIPS' : `${map.get(id) ?? id} (${id})`);
  }, [dataService, programYear]);

  const detailRow = detailPathwayId
    ? report.find(r => r.pathwayId === detailPathwayId) ?? null
    : null;
  const detailRank = detailRow
    ? report.findIndex(r => r.pathwayId === detailRow.pathwayId) + 1
    : 0;

  const whatIfActive = Object.keys(assumptions).length > 0;
  const best = report[0];

  if (!practiceId) {
    return (
      <div className="p-6">
        <Alert><Info className="h-4 w-4" /><AlertDescription>Select a practice to view the pathway report.</AlertDescription></Alert>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <div>
            <h1 className="text-2xl font-semibold">Reporting Pathway Report</h1>
            <p className="text-sm text-muted-foreground">
              {selectedPractice?.tin_name}{activeSubgroup ? ` · subgroup: ${activeSubgroup.name}` : ''} · PY{programYear} · projected scores across Traditional MIPS and all {report.length > 0 ? report.length - 1 : 27} MVPs
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {(subgroupsQ.data?.length ?? 0) > 0 && (
            <Select value={entityKey} onValueChange={setEntityKey}>
              <SelectTrigger className="w-56 h-9 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="group">Full practice (TIN)</SelectItem>
                {(subgroupsQ.data ?? []).map(sg => (
                  <SelectItem key={sg.id} value={`sg:${sg.id}`}>Subgroup: {sg.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button
            variant={whatIfActive ? 'default' : 'outline'}
            size="sm"
            onClick={() => setWhatIfOpen(o => !o)}
          >
            <FlaskConical className="h-4 w-4 mr-1" />
            {whatIfActive ? 'Assumptions active' : 'What-if assumptions'}
          </Button>
        </div>
      </div>

      {activeSubgroup && (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            Subgroup scope: quality is derived from this roster's {activeSubgroup.members.length} clinicians;
            cost, PI, and population health remain practice-level (CMS scores those at the affiliated group).
            Manual edits made in the group view aren't reflected here yet — subgroup numbers are estimates.
          </AlertDescription>
        </Alert>
      )}

      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          All figures are <strong>projections</strong> from your current data — not CMS results.
          Cost uses prior-year CMS feedback{meta.priorYearAvailable ? '' : ' (no score submission uploaded — improvement scoring unavailable)'};
          population health source: {meta.popHealthSource === 'none' ? 'not available (excluded from quality per CMS policy)' : meta.popHealthSource.replace('_', ' ')}.
          {meta.allowedCharges == null && ' Dollar impact unavailable — upload your QPP eligibility file or enter Medicare charges in Configuration.'}
        </AlertDescription>
      </Alert>

      {/* Inputs in use — always visible; defaults come from Configuration */}
      {ready && (
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className="text-muted-foreground">Inputs in use (from Configuration):</span>
          <Badge variant="secondary">{meta.inputsInUse.isSmallPractice ? 'Small practice' : 'Standard practice'}</Badge>
          <Badge variant="secondary">PI: {meta.inputsInUse.piSource}</Badge>
          <Badge variant="secondary">IA: {meta.inputsInUse.iaSource}</Badge>
          <Badge variant="secondary">Medicare charges: {meta.allowedCharges != null ? `$${Math.round(meta.allowedCharges).toLocaleString()} · ${meta.inputsInUse.chargesSource}` : 'not available'}</Badge>
          <Badge variant="secondary">CPB: +{meta.inputsInUse.cpb.toFixed(2)}</Badge>
          {whatIfActive && <Badge>what-if overrides active</Badge>}
        </div>
      )}

      {!meta.mvpDatasetAvailable && !loading && (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>No MVP catalog exists for PY{programYear} — MVPs launched for this registry in 2026. Switch the viewing year.</AlertDescription>
        </Alert>
      )}

      {/* What-if panel */}
      {whatIfOpen && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">What-if assumptions</CardTitle>
            <CardDescription>Hypothetical inputs re-rank every pathway through the identical engine. Nothing is saved.</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 md:grid-cols-4">
            <div className="flex items-center gap-2">
              <Switch
                id="wi-pi"
                checked={assumptions.willSubmitPI !== false}
                onCheckedChange={(v) => setAssumptions(a => ({ ...a, willSubmitPI: v }))}
              />
              <Label htmlFor="wi-pi" className="text-sm">Will submit PI data</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="wi-ia"
                checked={assumptions.iaWillAttest ?? true}
                onCheckedChange={(v) => setAssumptions(a => ({ ...a, iaWillAttest: v }))}
              />
              <Label htmlFor="wi-ia" className="text-sm">Will attest to an MVP activity</Label>
            </div>
            <div className="space-y-1">
              <Label className="text-sm">Pop health 479 points (0-10)</Label>
              <Input
                type="number" min={0} max={10} step={0.1} placeholder="live value"
                onChange={(e) => setAssumptions(a => ({
                  ...a,
                  popHealthOverride: { ...a.popHealthOverride, m479: e.target.value === '' ? undefined : Number(e.target.value) },
                }))}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-sm">Complex patient bonus (0-10)</Label>
              <Input
                type="number" min={0} max={10} step={0.1} placeholder="live value"
                onChange={(e) => setAssumptions(a => ({
                  ...a,
                  cpbOverride: e.target.value === '' ? undefined : Number(e.target.value),
                }))}
              />
            </div>
            <div className="md:col-span-4">
              <Button variant="ghost" size="sm" onClick={() => setAssumptions({})} disabled={!whatIfActive}>
                Reset to live data
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Best pathway callout */}
      {ready && best && (
        <Card className="border-primary/40">
          <CardContent className="pt-6 flex items-center gap-4">
            <Trophy className="h-8 w-8 text-primary shrink-0" />
            <div>
              <div className="text-sm text-muted-foreground">Best projected pathway{whatIfActive ? ' (under your assumptions)' : ''}</div>
              <div className="text-lg font-semibold">
                {titleFor(best.pathwayId)} — {best.finalScore.toFixed(1)} points
                {best.dollars.estImpact != null && <> · est. {fmtMoney(best.dollars.estImpact)}</>}
              </div>
              {report[1] && (
                <div className="text-sm text-muted-foreground">
                  {(best.finalScore - report[1].finalScore).toFixed(1)} points ahead of {titleFor(report[1].pathwayId)}
                  {best.dollars.estImpact != null && report[1].dollars.estImpact != null &&
                    <> · worth {fmtMoney(best.dollars.estImpact - report[1].dollars.estImpact)} vs. runner-up</>}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Ranked table */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">All pathways, ranked</CardTitle>
          <CardDescription>Registration closes Nov 30, {programYear}. Click a row to open the projection detail.</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2">{Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full table-fixed text-sm">
                <colgroup>
                  <col className="w-10" />
                  <col />
                  <col className="w-[5.5rem]" />
                  <col className="w-[5.5rem]" />
                  <col className="w-[5.5rem]" />
                  <col className="w-[5.5rem]" />
                  <col className="w-[5.5rem]" />
                  <col className="w-[4.5rem]" />
                  <col className="w-[6.5rem]" />
                  <col className="w-[5.5rem]" />
                  <col className="w-8" />
                </colgroup>
                <thead>
                  <tr className="text-left text-muted-foreground border-b">
                    <th className="py-2 pr-2">#</th>
                    <th className="py-2 pr-4">Pathway</th>
                    <th className="py-2 px-1 text-right">Quality</th>
                    <th className="py-2 px-1 text-right">Cost</th>
                    <th className="py-2 px-1 text-right">IA</th>
                    <th className="py-2 px-1 text-right">PI</th>
                    <th className="py-2 px-1 text-right">Projected</th>
                    <th className="py-2 px-1 text-right">vs 75</th>
                    <th className="py-2 px-1 text-right">Est. $</th>
                    <th className="py-2 px-1 text-center">Confidence</th>
                    <th className="py-2" />
                  </tr>
                </thead>
                <tbody>
                  {report.map((row, i) => {
                    const delta = row.finalScore - 75;
                    const isSelected = detailPathwayId === row.pathwayId;
                    return (
                      <tr
                        key={row.pathwayId}
                        className={`border-b cursor-pointer transition-colors hover:bg-muted/50 ${
                          isSelected ? 'bg-muted/40' : ''
                        } ${!row.eligibility.canReport ? 'opacity-60' : ''}`}
                        onClick={() => setDetailPathwayId(row.pathwayId)}
                      >
                        <td className="py-2.5 pr-2 text-muted-foreground">{i + 1}</td>
                        <td className="py-2.5 pr-4 truncate">
                          <span className={row.pathwayId === 'traditional' ? 'font-medium' : ''} title={titleFor(row.pathwayId)}>
                            {titleFor(row.pathwayId)}
                          </span>
                          {!row.eligibility.canReport && (
                            <Badge variant="outline" className="ml-2 text-xs">subgroups required</Badge>
                          )}
                        </td>
                        <td className="py-2.5 px-1 text-right tabular-nums whitespace-nowrap">{row.quality.points.toFixed(1)}<span className="text-muted-foreground">/{row.quality.weight}</span></td>
                        <td className="py-2.5 px-1 text-right tabular-nums whitespace-nowrap">{row.cost.pct == null ? '—' : <>{row.cost.points.toFixed(1)}<span className="text-muted-foreground">/{row.cost.weight}</span></>}</td>
                        <td className="py-2.5 px-1 text-right tabular-nums whitespace-nowrap">{row.ia.pct == null ? '—' : <>{row.ia.points.toFixed(1)}<span className="text-muted-foreground">/{row.ia.weight}</span></>}</td>
                        <td className="py-2.5 px-1 text-right tabular-nums whitespace-nowrap">{row.pi.pct == null ? '—' : <>{row.pi.points.toFixed(1)}<span className="text-muted-foreground">/{row.pi.weight}</span></>}</td>
                        <td className="py-2.5 px-1 text-right font-semibold tabular-nums whitespace-nowrap">{row.finalScore.toFixed(1)}</td>
                        <td className={`py-2.5 px-1 text-right tabular-nums whitespace-nowrap ${delta >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {delta >= 0 ? '+' : ''}{delta.toFixed(1)}
                        </td>
                        <td className="py-2.5 px-1 text-right tabular-nums whitespace-nowrap">
                          {row.dollars.estImpact == null ? '—' : fmtMoney(row.dollars.estImpact)}
                        </td>
                        <td className="py-2.5 px-1 text-center">
                          <Badge variant={confidence(row) === 'High' ? 'default' : 'secondary'} className="text-xs">{confidence(row)}</Badge>
                        </td>
                        <td className="py-2.5 text-muted-foreground">
                          <ChevronRight className="h-4 w-4 mx-auto" />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground">
        Projections use this registry's calculated quality data, prior-year CMS cost feedback, and your IA/PI inputs.
        Positive dollar figures depend on the CMS budget-neutrality scaling factor, set after the performance year closes.
        MVP registration is a manual step on the QPP website (window: Apr 1 – Nov 30, {programYear}; CAHPS by Jun 30).
      </p>

      <PathwayDetailDialog
        open={!!detailRow}
        onOpenChange={(open) => { if (!open) setDetailPathwayId(null); }}
        row={detailRow}
        rank={detailRank}
        title={detailRow ? titleFor(detailRow.pathwayId) : ''}
      />
    </div>
  );
}

function ScoreTile({
  label, points, weight, pct, emptyLabel,
}: {
  label: string;
  points: number;
  weight: number;
  pct: number | null;
  emptyLabel?: string;
}) {
  const empty = pct == null;
  return (
    <div className="rounded-xl border bg-slate-50/80 px-3 py-3 space-y-1">
      <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{label}</div>
      {empty ? (
        <div className="text-sm text-muted-foreground">{emptyLabel ?? '—'}</div>
      ) : (
        <>
          <div className="text-xl font-semibold tabular-nums tracking-tight">
            {points.toFixed(1)}
            <span className="text-sm font-normal text-muted-foreground">/{weight}</span>
          </div>
          <div className="text-xs text-muted-foreground tabular-nums">{fmtPct(pct)}</div>
        </>
      )}
    </div>
  );
}

function PathwayDetailDialog({
  open, onOpenChange, row, rank, title,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  row: PathwayResult | null;
  rank: number;
  title: string;
}) {
  if (!row) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl" />
      </Dialog>
    );
  }

  const q = row.quality.detail as any;
  const c = row.cost.detail as any;
  const ia = row.ia.detail as any;
  const delta = row.finalScore - 75;
  const conf = confidence(row);
  const selected = Array.isArray(q?.selected) ? q.selected : [];
  const costScored = Array.isArray(c?.scored) ? c.scored : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto gap-0 p-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b space-y-3">
          <div className="flex flex-wrap items-start justify-between gap-3 pr-6">
            <div className="space-y-1.5 min-w-0">
              <DialogTitle className="text-xl leading-snug pr-2">{title}</DialogTitle>
              <DialogDescription className="flex flex-wrap items-center gap-2">
                <Badge variant="secondary" className="font-normal">#{rank} projected</Badge>
                <Badge variant={conf === 'High' ? 'default' : 'secondary'} className="font-normal">
                  {conf} confidence
                </Badge>
                {row.pathwayId !== 'traditional' && (
                  <Badge className="bg-slate-800 text-white hover:bg-slate-800 font-mono font-normal">
                    {row.pathwayId}
                  </Badge>
                )}
                {!row.eligibility.canReport && (
                  <Badge variant="outline" className="font-normal">Subgroups required</Badge>
                )}
              </DialogDescription>
            </div>
            <div className="text-right shrink-0">
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Projected score</div>
              <div className="text-3xl font-semibold tabular-nums tracking-tight">{row.finalScore.toFixed(1)}</div>
              <div className={`text-sm tabular-nums ${delta >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                {delta >= 0 ? '+' : ''}{delta.toFixed(1)} vs 75
                {row.dollars.estImpact != null && (
                  <span className="text-muted-foreground"> · {fmtMoney(row.dollars.estImpact)}</span>
                )}
              </div>
            </div>
          </div>
        </DialogHeader>

        <div className="px-6 py-5 space-y-5">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <ScoreTile label="Quality" points={row.quality.points} weight={row.quality.weight} pct={row.quality.pct} />
            <ScoreTile
              label="Cost"
              points={row.cost.points}
              weight={row.cost.weight}
              pct={row.cost.pct}
              emptyLabel="Reweighted"
            />
            <ScoreTile
              label="IA"
              points={row.ia.points}
              weight={row.ia.weight}
              pct={row.ia.pct}
              emptyLabel="—"
            />
            <ScoreTile
              label="PI"
              points={row.pi.points}
              weight={row.pi.weight}
              pct={row.pi.pct}
              emptyLabel="—"
            />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <section className="rounded-xl border p-4 space-y-3">
              <div className="flex items-baseline justify-between gap-2">
                <h3 className="text-sm font-semibold">Quality measures</h3>
                <span className="text-xs text-muted-foreground tabular-nums">{fmtPct(row.quality.pct)}</span>
              </div>
              {selected.length > 0 ? (
                <ul className="divide-y rounded-lg border bg-white">
                  {selected.map((m: any) => (
                    <li
                      key={`${m.measureId}:${m.submissionMethod}`}
                      className="flex items-baseline justify-between gap-3 px-3 py-2 text-sm"
                    >
                      <span>
                        <span className="font-medium">{m.measureId}</span>
                        <span className="text-muted-foreground"> · {m.submissionMethod}</span>
                        {m.reservedSlot ? (
                          <span className="text-muted-foreground"> · outcome / high-priority slot</span>
                        ) : null}
                      </span>
                      <span className="tabular-nums font-medium shrink-0">{m.points.toFixed(1)} pts</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-muted-foreground">No scoreable measures in this pathway&apos;s pool.</p>
              )}
              {q?.leaveOut?.length > 0 && (
                <div className="flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800">
                  <Info className="mt-0.5 h-4 w-4 shrink-0" />
                  <p>
                    <span className="font-medium">Submission tip:</span> leave{' '}
                    {q.leaveOut.map((m: any) => `${m.measureId} (${m.points.toFixed(1)} pts)`).join(', ')} out of your
                    CMS submission. {selected.find((m: any) => m.reservedSlot)?.measureId} fills the
                    outcome/high-priority requirement, so your best {selected.length > 4 ? 'six' : 'four'} measures count.
                  </p>
                </div>
              )}
              <div className="space-y-1 text-xs text-muted-foreground">
                {q?.unfilledSlots > 0 && (
                  <p>{q.unfilledSlots} required slot(s) unreported — scored 0/10 each.</p>
                )}
                <p>
                  Population health:{' '}
                  {q?.popHealth?.scored
                    ? `measure ${q.popHealth.measureId} · ${q.popHealth.points} pts (+10 denominator)`
                    : (q?.popHealth?.reason ?? 'n/a')}
                </p>
                {q?.smallPracticeBonus > 0 && (
                  <p>Small-practice bonus: +{q.smallPracticeBonus} (numerator only)</p>
                )}
                {q?.improvementPP > 0 && (
                  <p>Improvement: +{q.improvementPP.toFixed(2)}pp</p>
                )}
                {Array.isArray(q?.qcdrExcluded) && q.qcdrExcluded.length > 0 && (
                  <p>QCDR not calculated here: {q.qcdrExcluded.join(', ')}</p>
                )}
              </div>
            </section>

            <div className="space-y-4">
              <section className="rounded-xl border p-4 space-y-3">
                <div className="flex items-baseline justify-between gap-2">
                  <h3 className="text-sm font-semibold">Cost</h3>
                  <span className="text-xs text-muted-foreground tabular-nums">
                    {row.cost.pct == null ? 'reweighted to 0%' : fmtPct(row.cost.pct)}
                  </span>
                </div>
                {costScored.length > 0 ? (
                  <ul className="divide-y rounded-lg border bg-white">
                    {costScored.map((m: any) => (
                      <li key={m.measureId} className="flex justify-between gap-3 px-3 py-2 text-sm">
                        <span className="font-medium">{m.measureId}</span>
                        <span className="tabular-nums font-medium">{m.points.toFixed(1)} pts</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    No scoreable cost measure in this pathway — weight redistributed.
                  </p>
                )}
              </section>

              <section className="rounded-xl border p-4 space-y-2">
                <div className="flex items-baseline justify-between gap-2">
                  <h3 className="text-sm font-semibold">Improvement activities</h3>
                  <span className="text-xs text-muted-foreground tabular-nums">
                    {row.ia.pct == null ? '—' : fmtPct(row.ia.pct, 0)}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground">{ia?.hint ?? ia?.source ?? '—'}</p>
              </section>

              <section className="rounded-xl border p-4 space-y-2">
                <h3 className="text-sm font-semibold">Projection notes</h3>
                <p className="text-xs text-muted-foreground">
                  Weight set <code className="rounded bg-muted px-1 py-0.5">{row.audit.weightSetKey}</code>
                  {' '}· CPB +{row.cpb.toFixed(2)}
                </p>
                {row.flags.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {row.flags.map(f => FLAG_LABELS[f] ? (
                      <Badge key={f} variant="outline" className="font-normal text-xs">
                        {FLAG_LABELS[f].label}
                      </Badge>
                    ) : null)}
                  </div>
                )}
                {row.eligibility.reasons.length > 0 && (
                  <p className="text-xs text-muted-foreground">{row.eligibility.reasons.join(' · ')}</p>
                )}
              </section>
            </div>
          </div>

          <p className="text-xs text-muted-foreground border-t pt-3">
            Projection from current registry data — not a CMS result. Dollar impact depends on the
            post-year budget-neutrality scaling factor.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
